import img from "@/assets/img/evesicon.png";

import { StatusCodes } from "@/common/constants/constants";
import { HTTPError, RestResponse } from "@/common/enums/enums";
import { disableLoaderForOnce } from "@/common/utils/loadingService";
import store from "@/store";
import { setLoader } from "@/store/app/reducer";
import {
  blobToBase64String,
  buildMobileAppDeepLink,
  getSubdomain,
  handleError,
  handleHttpError,
  isInMobileApp,
} from "@/common/utils/utils";
import { IContextPropsModel, withContext } from "@/common/utils/withContext";
import {
  getTenantLogoBySubdomain,
  resendVerificationEmail as resendVerificationEmailToUser,
  verifyUserEmail,
} from "@/services/auth";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import styles from "./verifyEmailV2.module.scss";
import { Loader } from "@/common/components/atoms/loader/loader";
import { IAppContextModel } from "@/store/app/types";
import { useSelector } from "react-redux";

const VerifyEmailV2 = (props: IContextPropsModel): JSX.Element => {
  const appContext: IAppContextModel = useSelector(
    (state: any) => state.appContext,
  );
  const [state, setStateData] = useState({
    isVisible: false,
    tenantLogo: img,
  });
  const subdomain = getSubdomain();
  const search = useLocation().search;
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  let mobileAppURL: string;

  const searchParams = new URLSearchParams(search);
  const verificationToken =
    searchParams.get("VerificationToken") || searchParams.get("token");
  const resetToken = searchParams.get("token");
  const userEmail = searchParams.get("Email") || searchParams.get("username");
  const userDomain = searchParams.get("userdomain") || subdomain;

  if (isInMobileApp(subdomain || "")) {
    mobileAppURL =
      buildMobileAppDeepLink(
        `verifyAccount/${subdomain}/${userEmail}/${verificationToken}/${resetToken}`,
      ) || "";
    window.location.href = mobileAppURL;
  }

  const verifyEmail = (email: string, token: string) => {
    return verifyUserEmail({
      Email: email,
      VerificationToken: token,
      tenant: userDomain || "",
    });
  };

  const [status, setStatus] = useState<"success" | "error">();
  const [countdown, setCountdown] = useState<number>(5);
  const hasCalledVerify = useRef(false);

  const performVerify = (callEmail?: string, token?: string) => {
    const emailToUse = callEmail ?? userEmail;
    const tokenToUse = token ?? verificationToken;
    if (!emailToUse || !tokenToUse) return;
    store.dispatch(setLoader(true));
    verifyEmail(emailToUse, tokenToUse)
      .then((response: any) => {
        disableLoaderForOnce();
        if (response && response.status === RestResponse.SUCCESS) {
          setStatus("success");
          setCountdown(5);
        } else {
          setStatus("error");
        }
      })
      .catch(() => {
        disableLoaderForOnce();
        setStatus("error");
      });
  };

  useEffect(() => {
    if (status === "success") {
      const timer = setInterval(() => {
        setCountdown((c) => c - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [status]);

  useEffect(() => {
    if (status === "success" && countdown <= 0) {
      navigate({ pathname: "/login" });
    }
  }, [countdown, status]);

  const resendVerificationEmail = (data: any) => {
    resendVerificationEmailToUser(data)
      .then((response: any) => {
        disableLoaderForOnce();
        if (response.status && response.status === RestResponse.SUCCESS) {
          props.notificationService?.notify({
            message: translate("authentication.verify_email_resend_success"),
            type: "success",
          });
          navigate({ pathname: "/login" });
        } else {
          handleError(
            JSON.stringify(response),
            translate("authentication.verify_email_resend_error") || "",
          );
        }
      })
      .catch((error: any) => {
        disableLoaderForOnce();
        switch (error.status) {
          case HTTPError.USER_ACCOUNT_ALREADY_ACTIVE_ERROR:
            props.notificationService?.notify({
              message: translate("authentication.verify_email_already_active"),
              type: "info",
            });
            navigate({ pathname: "/login" });
            break;
          case StatusCodes.NOT_FOUND:
            props.notificationService?.notify({
              message: translate("authentication.verify_email_email_not_valid"),
              type: "error",
            });
            break;
          default:
            handleHttpError(
              error,
              translate("authentication.verify_email_resend_error"),
            );
            break;
        }
      });
  };

  const { refetch: fetchTenantLogo } = useQuery(
    "tenantLogo",
    () => {
      if (subdomain) {
        return getTenantLogoBySubdomain(subdomain);
      }
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: async (data: any) => {
        if (data) {
          if (data?.status >= 400 && data?.status < 500) {
            setStateData((currentData) => ({
              ...currentData,
              tenantLogo: img,
            }));
          } else {
            const base64String = await blobToBase64String(data);
            setStateData((currentData) => ({
              ...currentData,
              tenantLogo: base64String,
            }));
          }
        }
      },
      onError: () => {
        setStateData((currentData) => ({
          ...currentData,
          tenantLogo: img,
        }));
      },
    },
  );

  useEffect(() => {
    fetchTenantLogo();
    if (
      userEmail &&
      verificationToken &&
      !isInMobileApp(subdomain || "") &&
      !hasCalledVerify.current
    ) {
      hasCalledVerify.current = true;
      performVerify();
    }
  }, [userEmail, verificationToken, subdomain]);

  const goToLogin = () => navigate({ pathname: "/login" });

  if (appContext?.showLoader) {
    return <Loader showLoader={appContext?.showLoader} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        <div className={styles.logoWrap}>
          <img
            src={state.tenantLogo}
            alt="EVES"
            width={"136px"}
            height={"119px"}
          />
        </div>

        {status === "success" && (
          <>
            <h1 className={styles.heading}>
              {translate("authentication.verify_email_success_title") ||
                "Email Verified Successfully"}
            </h1>
            <p className={`${styles.subtext} ${styles.successSubtext}`}>
              {translate("authentication.verify_email_success_subtitle") ||
                "Your account is now active and ready to use. We'll redirect you to the login page shortly."}
            </p>
            <button
              type="button"
              className={styles.btnLogin}
              onClick={goToLogin}
              aria-label="Go to Login"
            >
              <ArrowBackIcon sx={{ fontSize: 20 }} />
              {translate("authentication.verify_email_go_to_login") ||
                "Go to Login"}
            </button>
          </>
        )}

        {status === "error" && (
          <>
            <h1 className={styles.heading}>
              {translate("authentication.verify_email_error_title") ||
                "Email Verification Failed"}
            </h1>
            <p className={`${styles.subtext} ${styles.errorSubtext}`}>
              {translate("authentication.verify_email_error_intro")}
            </p>
            <button
              type="button"
              className={styles.btnLogin}
              onClick={() =>
                resendVerificationEmail({
                  email: userEmail,
                  Tenant: userDomain,
                })
              }
            >
              {translate("authentication.verify_email_try_again") ||
                "Resend Verification Link"}
            </button>
            <p className={styles.supportLine}>
              {translate("authentication.verify_email_need_help") ||
                "Need help?"}{" "}
              <a
                href="mailto:support@dynachrg.ai"
                className={styles.supportLink}
                rel="noopener noreferrer"
              >
                {translate("authentication.verify_email_contact_support") ||
                  "Contact Support"}
              </a>
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default withContext(VerifyEmailV2);
